import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ProjectDetails } from '@/types/project';

interface ProjectDetailsFormProps {
  details: ProjectDetails;
  onChange: (updates: Partial<ProjectDetails>) => void;
}

export function ProjectDetailsForm({ details, onChange }: ProjectDetailsFormProps) {
  return (
    <div className="space-y-6">
      {/* Project Name and Client Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="projectName">Project Name</Label>
          <Input
            id="projectName"
            value={details.projectName || ''}
            onChange={(e) => onChange({ projectName: e.target.value })}
            placeholder="Enter project name"
            aria-required="true"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="clientName">Client Name</Label>
          <Input
            id="clientName"
            value={details.clientName || ''}
            onChange={(e) => onChange({ clientName: e.target.value })}
            placeholder="Enter client name"
            aria-required="true"
          />
        </div>
      </div>

      {/* Project Start and End Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Project Start Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !details.projectStartDate && "text-muted-foreground"
                )}
                aria-label="Select project start date"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {details.projectStartDate ? format(details.projectStartDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={details.projectStartDate}
                onSelect={(date) => onChange({ projectStartDate: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Project End Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !details.projectEndDate && "text-muted-foreground"
                )}
                aria-label="Select project end date"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {details.projectEndDate ? format(details.projectEndDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={details.projectEndDate}
                onSelect={(date) => onChange({ projectEndDate: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Reporting Period */}
      <div>
        <Label>Reporting Period</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !details.reportingPeriodStart && "text-muted-foreground"
                )}
                aria-label="Select reporting period start date"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {details.reportingPeriodStart ? format(details.reportingPeriodStart, "PPP") : <span>Start date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={details.reportingPeriodStart}
                onSelect={(date) => onChange({ reportingPeriodStart: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !details.reportingPeriodEnd && "text-muted-foreground"
                )}
                aria-label="Select reporting period end date"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {details.reportingPeriodEnd ? format(details.reportingPeriodEnd, "PPP") : <span>End date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={details.reportingPeriodEnd}
                onSelect={(date) => onChange({ reportingPeriodEnd: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}